import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../store';
import { selectShiftPlanData } from '../../store/slices/shiftPlanSlice';
import { checkCheckoutFeatureEnabled } from '../../store/slices/checkoutSlice';
import { ROUTES } from '../../utils/constants';
import ShiftPlanTabs from '../shiftPlanTabs';

// Import page components
import RosterAndRates from '../../pages/rosterAndRates';
import VolumesAndWip from '../../pages/volumesAndWip/VolumesAndWipContainer';
import Checkout from '../../pages/checkout';
import ReviewAndPublish from '../../pages/reviewAndPublish/reviewAndPublishContainer';

/**
 * ShiftPlanLayout component that provides persistent tabs and nested routing
 * for the shift planning workflow. Reads date/shift from Redux store.
 */
const ShiftPlanLayout: React.FC = () => {
  const dispatch = useAppDispatch();

  // Get shift plan data from Redux store
  const { selectedDate, selectedShift, selectedPlan, isShiftPlanActive } = useAppSelector(selectShiftPlanData);
  const isCheckoutEnabled = useAppSelector((state) => state.checkout.isCheckoutEnabled);

  // Check checkout feature flag on mount
  useEffect(() => {
    if (isCheckoutEnabled === null) {
      dispatch(checkCheckoutFeatureEnabled());
    }
  }, [dispatch, isCheckoutEnabled]);

  // If no shift plan is active, redirect to the main page
  if (!isShiftPlanActive || !selectedDate || !selectedShift) {
    return <Navigate to="/" replace />;
  }

  // Wait for feature flag to resolve before rendering routes/tabs
  if (isCheckoutEnabled === null) {
    return null;
  }

  return (
    <ShiftPlanTabs
      date={selectedDate}
      shiftType={selectedShift}
      planType={selectedPlan}
      description="This page allows you to run and view the next shift plan and accept or override it."
    >
      <Routes>
        {/* Default route - redirect to roster */}
        <Route path="/" element={<Navigate to={ROUTES.SHIFT_PLAN_ROSTER} replace />} />
        
        {/* Tab content routes */}
        <Route path="/roster" element={<RosterAndRates />} />
        <Route path="/volumes" element={<VolumesAndWip />} />
        {isCheckoutEnabled && <Route path="/checkout" element={<Checkout />} />}
        <Route path="/review" element={<ReviewAndPublish />} />
        
        {/* Fallback route */}
        <Route path="*" element={<Navigate to={ROUTES.SHIFT_PLAN_ROSTER} replace />} />
      </Routes>
    </ShiftPlanTabs>
  );
};

export default ShiftPlanLayout;
